import { get, set } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import dayjs from 'dayjs'
/**
 * @class Recruitment
 * @description Represents a recruitment record with full and compact data layers.
 * Supports MobX observability, CRUD-style updates, and synchronization from server data.
 */
export class Recruitment {
	_class = 'Recruitment'
	loaded = -1
	recruitment_id = ''
	compact = {}
	full = {}
	to_delete = false
	modified = null

	/**
	 * @constructor
	 * @param {string} id - Recruitment ID used to initialize the instance.
	 */
	constructor(id) {
		this.recruitment_id = id
		this.compact = {}
		this.full = {}

		makeObservable(this, {
			recruitment_id: observable,
			compact: observable,
			full: observable,
			to_delete: observable,
			modified: observable,

			// Computed properties for accessing data in compact/full
			name: computed,
			email: computed,
			phone: computed,
			highest_education: computed,
			cv: computed,
			linkedin: computed,
			facebook: computed,
			website: computed,
			progress: computed,
			createdBy: computed,
			createdTs: computed,
			modifiedTs: computed,

			// Action methods for setting properties
			setName: action.bound,
			setEmail: action.bound,
			setPhone: action.bound,
			setHighestEducation: action.bound,
			setCv: action.bound,
			setLinkedin: action.bound,
			setFacebook: action.bound,
			setWebsite: action.bound,
			setProgress: action.bound,
			setCreatedBy: action.bound,
			setCreatedTs: action.bound,
			setToDelete: action.bound,
			setModified: action.bound,

			updateFromServer: action.bound,
			// Loaded
			loaded: observable,
			resetLastFullLoad: action.bound
		})
	}

	/**
	 * get name
	 * @description Retrieves the candidate's name from full or compact data.
	 * @returns {string}
	 */
	get name() {
		return get(this.full, 'name', get(this.compact, 'name', ''))
	}

	/**
	 * setName
	 * @description Sets the candidate's name in the full object.
	 * @param {string} value
	 */
	setName(value) {
		this.chgInFull('name', value)
	}

	/**
	 * get email
	 * @description Retrieves the candidate's email from full or compact data.
	 * @returns {string}
	 */
	get email() {
		return get(this.full, 'email', get(this.compact, 'email', ''))
	}

	/**
	 * setEmail
	 * @description Sets the candidate's email in the full object.
	 * @param {string} value
	 */
	setEmail(value) {
		this.chgInFull('email', value)
	}

	/**
	 * get phone
	 * @description Retrieves the candidate's phone number from full or compact data.
	 * @returns {string}
	 */
	get phone() {
		return get(this.full, 'phone', get(this.compact, 'phone', ''))
	}

	/**
	 * setPhone
	 * @description Sets the candidate's phone number in the full object.
	 * @param {string} value
	 */
	setPhone(value) {
		this.chgInFull('phone', value)
	}

	/**
	 * get highest_education
	 * @description Retrieves the candidate's highest education from full or compact data.
	 * @returns {string}
	 */
	get highest_education() {
		return get(
			this.full,
			'highest_education',
			get(this.compact, 'highest_education', '')
		)
	}

	/**
	 * setHighestEducation
	 * @description Sets the candidate's highest education level.
	 * @param {string} value
	 */
	setHighestEducation(value) {
		this.chgInFull('highest_education', value)
	}

	/**
	 * get cv
	 * @description Retrieves the list of attached CVs or resumes from full or compact data.
	 * @returns {Array}
	 */
	get cv() {
		return get(this.full, 'cv', get(this.compact, 'cv', []))
	}

	/**
	 * setCv
	 * @description Sets the list of attached CVs in the full object.
	 * @param {Array} value
	 */
	setCv(value) {
		this.chgInFull('cv', value)
	}

	/**
	 * get linkedin
	 * @description Retrieves the LinkedIn URL from full or compact data.
	 * @returns {string}
	 */
	get linkedin() {
		return get(this.full, 'linkedin', get(this.compact, 'linkedin', ''))
	}

	/**
	 * setLinkedin
	 * @description Sets the LinkedIn profile URL.
	 * @param {string} value
	 */
	setLinkedin(value) {
		this.chgInFull('linkedin', value)
	}

	/**
	 * get facebook
	 * @description Retrieves the Facebook URL from full or compact data.
	 * @returns {string}
	 */
	get facebook() {
		return get(this.full, 'facebook', get(this.compact, 'facebook', ''))
	}

	/**
	 * setFacebook
	 * @description Sets the Facebook profile URL.
	 * @param {string} value
	 */
	setFacebook(value) {
		this.chgInFull('facebook', value)
	}

	/**
	 * get website
	 * @description Retrieves the personal or portfolio website URL from full or compact data.
	 * @returns {string}
	 */
	get website() {
		return get(this.full, 'website', get(this.compact, 'website', ''))
	}

	/**
	 * setWebsite
	 * @description Sets the website URL in the full object.
	 * @param {string} value
	 */
	setWebsite(value) {
		this.chgInFull('website', value)
	}

	/**
	 * get progress
	 * @description Retrieves the current recruitment progress status.
	 * @returns {string}
	 */
	get progress() {
		return get(this.full, 'progress', get(this.compact, 'progress', ''))
	}

	/**
	 * setProgress
	 * @description Sets the current recruitment progress status.
	 * @param {string} value
	 */
	setProgress(value) {
		this.chgInFull('progress', value)
	}

	/**
	 * get createdBy
	 * @description Returns the user ID of the creator of this recruitment record.
	 * @returns {string}
	 */
	get createdBy() {
		return get(this.full, 'createdBy', get(this.compact, 'createdBy', ''))
	}

	/**
	 * setCreatedBy
	 * @description Sets the user ID of the creator.
	 * @param {string} value
	 */
	setCreatedBy(value) {
		this.chgInFull('createdBy', value)
	}

	/**
	 * get createdTs
	 * @description Returns the creation timestamp as Unix time.
	 * @returns {number|null}
	 */
	get createdTs() {
		const createdTs = get(
			this.full,
			'createdTs',
			get(this.compact, 'createdTs', null)
		)
		return createdTs ? dayjs(createdTs).unix() : null
	}

	/**
	 * setCreatedTs
	 * @description Sets the creation timestamp.
	 * @param {string} value - Timestamp string.
	 */
	setCreatedTs(value) {
		this.chgInFull('createdTs', value)
	}

	/**
	 * get modifiedTs
	 * @description Returns the last modification timestamp as Unix time.
	 * @returns {number|null}
	 */
	get modifiedTs() {
		return this.modified ? dayjs(this.modified).unix() : null
	}

	/**
	 * setModified
	 * @description Sets the modified timestamp.
	 * @param {string} value - Timestamp string.
	 */
	setModified(value) {
		this.modified = value
	}

	/**
	 * setToDelete
	 * @description Marks the recruitment record for deletion or restores it.
	 * @param {boolean|number} value - Either `false` or a future Unix timestamp.
	 */
	setToDelete(value) {
		this.to_delete = value
	}

	// 2. Basic Methods applicable to all
	/**
	 * Sets the loaded flag to -2.
	 */
	setNewFlag = () => {
		this.loaded = -2
	}

	/**
	 * Resets the loaded flag to the current timestamp.
	 */
	resetLastFullLoad = () => {
		this.loaded = dayjs().unix()
	}

	/**
	 * Update properties based on server data.
	 * @param {object} srvJson - Data from the server
	 */
	updateFromServer(srvJson) {
		if (typeof srvJson.compact === 'string') {
			try {
				this.compact = JSON.parse(srvJson.compact)
				this.full = this.compact || {}
			} catch (error) {
				console.error('Failed to parse compact JSON:', error)
				this.compact = {}
			}
		} else {
			this.compact = srvJson.compact || {}
			this.full = srvJson.compact || {}
		}

		if (typeof srvJson.full === 'string') {
			try {
				this.full = JSON.parse(srvJson.full)
			} catch (error) {
				console.error('Failed to parse full JSON:', error)
				this.full = {}
			}
		} else {
			this.full = srvJson.full || {}
		}

		this.to_delete = srvJson.to_delete || false
		this.modified = srvJson.modified || null
	}

	/**
	 * Helper to update the full object with MobX observability.
	 * @param {string} path - Path within `full`
	 * @param {*} value - Value to set
	 */
	chgInFull(path, value) {
		if (get(this.full, path) !== value) {
			set(this.full, path, value)
		}
	}
}
